import { useEffect, useId, useRef, useState } from "react";
import type {
  CanvasAssignmentCandidateInput,
  ParentCandidateDetailsSnapshot,
  SaveCandidateDetailsCommand,
} from "@vsta/contracts";
import { CandidateDetails, effectiveCandidate } from "./candidate-details.js";

export interface SchoolworkOverviewProps {
  loadCandidates: () => Promise<CanvasAssignmentCandidateInput[]>;
  onReview?: () => void;
  timezone: string;
  audience?: "parent" | "student";
  onStart?:
    ((candidate: CanvasAssignmentCandidateInput) => Promise<void>) | undefined;
  startDisabled?: boolean;
  startLabel?: string;
  activeQueueItemId?: string | null;
  onShowTimer?: () => void;
  onSaveDetails?: (
    command: SaveCandidateDetailsCommand,
  ) => Promise<ParentCandidateDetailsSnapshot>;
}

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; candidates: CanvasAssignmentCandidateInput[] };

function localDate(value: string | undefined, timezone: string): string {
  if (value === undefined || !Number.isFinite(Date.parse(value))) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const part = (type: string) =>
    parts.find((item) => item.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function dateLabel(value: string, long = false): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: long ? "long" : "short",
    day: "numeric",
    ...(long ? { weekday: "long" as const } : {}),
  }).format(new Date(`${value}T12:00:00Z`));
}

export function SchoolworkOverview({
  loadCandidates,
  onReview,
  timezone,
  audience = "parent",
  onSaveDetails,
  onStart,
  startDisabled = false,
  startLabel = "Start working",
  activeQueueItemId,
  onShowTimer,
}: SchoolworkOverviewProps) {
  const titleId = useId();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [starting, setStarting] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const loadVersion = useRef(0);
  const [dateFilter, setDateFilter] = useState(
    audience === "student" ? "upcoming" : "all",
  );

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setState({ status: "loading" });
    const refresh = () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      const version = ++loadVersion.current;
      clearTimeout(timer);
      void loadCandidates()
        .then((candidates) => {
          if (!cancelled && version === loadVersion.current) {
            setState({ status: "loaded", candidates });
            setRefreshFailed(false);
          }
        })
        .catch(() => {
          if (!cancelled && version === loadVersion.current) {
            setState((previous) =>
              previous.status === "loaded" ? previous : { status: "error" },
            );
            setRefreshFailed(true);
          }
        })
        .finally(() => {
          inFlight = false;
          if (!cancelled && audience === "student")
            timer = setTimeout(refresh, 30_000);
        });
    };
    refresh();
    if (audience === "student") window.addEventListener("focus", refresh);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [loadCandidates, attempt, audience]);

  const candidates = state.status === "loaded" ? state.candidates : [];
  const groups = new Map<string, CanvasAssignmentCandidateInput[]>();
  for (const candidate of candidates) {
    const date = localDate(effectiveCandidate(candidate).dueAt, timezone);
    const group = groups.get(date) ?? [];
    group.push(candidate);
    groups.set(date, group);
  }
  const dates = [...groups.keys()].filter(Boolean).sort();
  const first = dates[0];
  const last = dates.at(-1);
  const courseCount = new Set(candidates.map((item) => item.sourceCourseKey))
    .size;
  const orderedDates = groups.has("") ? [...dates, ""] : dates;
  const today = localDate(new Date(Date.now()).toISOString(), timezone);

  return (
    <section
      className="schoolwork-overview empty-panel"
      aria-labelledby={titleId}
    >
      <div>
        {audience === "parent" ? (
          <p className="eyebrow">Schoolwork library</p>
        ) : null}
        <h2 id={titleId}>
          {audience === "student"
            ? "Schoolwork from Canvas"
            : "Imported activities"}
        </h2>
        {state.status === "loading" ? (
          <p role="status">Checking saved schoolwork…</p>
        ) : null}
        {state.status === "error" ? (
          <div>
            <p role="alert">
              Saved schoolwork could not be checked. This does not mean your
              imported activities are missing.
            </p>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setAttempt((value) => value + 1)}
            >
              Retry saved schoolwork
            </button>
          </div>
        ) : null}
        {state.status === "loaded" && candidates.length === 0 ? (
          <div>
            <h3>No imported activities yet</h3>
            <p>
              {audience === "student"
                ? "Schoolwork will appear here automatically after an import."
                : "Import a saved Canvas Calendar page. The activities will appear on the Student Console automatically."}
            </p>
            {onReview === undefined ? null : (
              <button type="button" onClick={onReview}>
                Import schoolwork or add an Assignment
              </button>
            )}
          </div>
        ) : null}
        {state.status === "loaded" && candidates.length > 0 ? (
          <div>
            <p role="status">
              <strong>
                {candidates.length}{" "}
                {candidates.length === 1 ? "activity" : "activities"} across{" "}
                {courseCount} {courseCount === 1 ? "Course" : "Courses"}
              </strong>
            </p>
            {audience === "student" ? null : first === undefined ||
              last === undefined ? (
              <p>No source dates provided.</p>
            ) : (
              <p>
                Activity dates: {dateLabel(first)}
                {first === last ? "" : ` – ${dateLabel(last)}`} · {timezone}
              </p>
            )}
            <p>
              {audience === "student"
                ? "Choose Start working when you are ready to track your work here. Viewing this list starts no timers. A 20-minute app check-in is used unless a parent sets another checkpoint; it is not a school time estimate."
                : "These activities are already available for planning and student timing. No extra setup is required. Add or edit optional parent details below, even while the student works. Customize a Daily Queue only if you want a more detailed plan."}
            </p>
            {state.status === "loaded" && refreshFailed ? (
              <p role="alert">
                The latest update could not be checked. Showing the last saved
                list; reconnect to receive changes.
              </p>
            ) : null}
            {onReview === undefined ? null : (
              <button type="button" onClick={onReview}>
                Review imported activities
              </button>
            )}
            {startError === null ? null : <p role="alert">{startError}</p>}
            <details
              className="schoolwork-agenda"
              open={audience === "student"}
            >
              <summary>
                {audience === "student"
                  ? "Activities by source date"
                  : "Browse saved activities by date"}
              </summary>
              {audience === "parent" ? (
                <p>
                  Dates include imported due or calendar dates and any parent
                  corrections. They are not confirmed work times or completion
                  records.
                </p>
              ) : null}
              {audience === "student" ? (
                <label className="import-search">
                  <span>Show activities for</span>
                  <select
                    value={dateFilter}
                    onChange={(event) => setDateFilter(event.target.value)}
                  >
                    <option value="upcoming">Today and upcoming</option>
                    <option value="all">
                      All imported dates, including earlier work
                    </option>
                    {orderedDates.map((date) => (
                      <option key={date || "undated"} value={date || "undated"}>
                        {date === ""
                          ? "Date not provided"
                          : dateLabel(date, true)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {orderedDates
                .filter(
                  (date) =>
                    dateFilter === "all" ||
                    (dateFilter === "upcoming" &&
                      (date === "" || date >= today)) ||
                    dateFilter === (date || "undated"),
                )
                .map((date) => (
                  <section
                    key={date || "undated"}
                    aria-label={
                      date === "" ? "Date not provided" : dateLabel(date, true)
                    }
                  >
                    <h3>
                      {date === ""
                        ? "Date not provided"
                        : dateLabel(date, true)}
                    </h3>
                    <ul>
                      {(groups.get(date) ?? []).map((candidate) => (
                        <li key={candidate.sourceItemKey}>
                          <strong>{effectiveCandidate(candidate).title}</strong>
                          {" · "}
                          {candidate.courseTitle}
                          {candidate.parentDetails?.values.notes ===
                          undefined ? null : (
                            <p>{candidate.parentDetails.values.notes}</p>
                          )}
                          {audience === "parent" &&
                          onSaveDetails !== undefined ? (
                            <CandidateDetails
                              candidate={candidate}
                              onSave={async (command) => {
                                const parentDetails =
                                  await onSaveDetails(command);
                                setState((current) =>
                                  current.status !== "loaded"
                                    ? current
                                    : {
                                        status: "loaded",
                                        candidates: current.candidates.map(
                                          (item) =>
                                            item.sourceItemKey ===
                                            command.sourceItemKey
                                              ? { ...item, parentDetails }
                                              : item,
                                        ),
                                      },
                                );
                                return parentDetails;
                              }}
                            />
                          ) : null}
                          {audience === "student" ? (
                            <div>
                              {candidate.tracking !== undefined &&
                              [
                                "done",
                                "deferred",
                                "skipped",
                                "replaced",
                              ].includes(candidate.tracking.status) ? (
                                <span>
                                  Recorded in your day:{" "}
                                  {candidate.tracking.status}
                                </span>
                              ) : candidate.tracking !== undefined &&
                                candidate.tracking.queueItemId ===
                                  activeQueueItemId ? (
                                onShowTimer === undefined ? (
                                  <span>
                                    Current activity — use the timer above.
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    aria-label={`Show current timer: ${effectiveCandidate(candidate).title}`}
                                    onClick={onShowTimer}
                                  >
                                    Show current timer
                                  </button>
                                )
                              ) : onStart === undefined ? null : (
                                <button
                                  type="button"
                                  aria-label={`${startLabel}: ${effectiveCandidate(candidate).title}`}
                                  disabled={startDisabled || starting !== null}
                                  onClick={() => {
                                    setStartError(null);
                                    setStarting(candidate.sourceItemKey);
                                    void onStart(candidate)
                                      .then(async () => {
                                        const version = ++loadVersion.current;
                                        try {
                                          const candidates =
                                            await loadCandidates();
                                          if (version === loadVersion.current) {
                                            setState({
                                              status: "loaded",
                                              candidates,
                                            });
                                            setRefreshFailed(false);
                                          }
                                        } catch {
                                          if (version === loadVersion.current)
                                            setRefreshFailed(true);
                                        }
                                      })
                                      .catch((reason: unknown) =>
                                        setStartError(
                                          reason instanceof Error
                                            ? reason.message
                                            : "Work could not be started. Try again.",
                                        ),
                                      )
                                      .finally(() => setStarting(null));
                                  }}
                                >
                                  {starting === candidate.sourceItemKey
                                    ? "Starting…"
                                    : startLabel}
                                </button>
                              )}
                              {effectiveCandidate(candidate).sourceUrl !==
                                undefined &&
                              /^https?:\/\//i.test(
                                effectiveCandidate(candidate).sourceUrl ?? "",
                              ) ? (
                                <a
                                  href={effectiveCandidate(candidate).sourceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Open {effectiveCandidate(candidate).title} in
                                  Canvas
                                </a>
                              ) : (
                                <span>
                                  Use your Canvas calendar to open this
                                  activity.
                                </span>
                              )}
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              {dateFilter === "upcoming" &&
              !orderedDates.some((date) => date === "" || date >= today) ? (
                <p>
                  No current or upcoming dates in this saved import. Choose All
                  imported dates to see earlier work.
                </p>
              ) : null}
            </details>
          </div>
        ) : null}
      </div>
    </section>
  );
}
